/**
 * Field Matching Service
 *
 * This file handles the logic for checking and matching existing fields
 * when importing a solution.
 */

import { createEmptyFieldUpdates } from "../utils/solutionImportHelpers";

/**
 * Check and match existing contact fields
 */
export const checkMatchingContactFields = async (
  importedJSON: any,
  findMatchingContactFields: (data: any) => Promise<any>,
  updates: Record<string, Record<string, string>>,
  setAutomaticallySelectedFields: React.Dispatch<React.SetStateAction<string[]>>
): Promise<void> => {
  const contactFieldPromises = Object.entries(
    importedJSON.Bag.External.ContactField || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    const contactField = JSON.parse(fieldKey);
    const contactFieldsData = await findMatchingContactFields(contactField);
    if (contactFieldsData?.data?.fields?.length > 0) {
      const matchingField =
        contactFieldsData.data.fields.find(
          (c: any) => c.label === contactField.label
        ) || contactFieldsData.data.fields[0];
      updates.ContactField[fieldKey] = matchingField.uuid;
      setAutomaticallySelectedFields((prev) => [...prev, fieldKey]);
      if (matchingField.options && matchingField.options.length > 0) {
        matchingField.options.forEach((field: any) => {
          const fieldData = {
            ...JSON.parse(fieldKey).options.find(
              (f: any) => f.label === field.label
            ),
          };
          delete fieldData.uuid;
          delete fieldData.order;
          if (fieldData) {
            updates.ContactFieldOption[
              JSON.stringify({ field: contactField, option: fieldData })
            ] = field.uuid;
          }
        });
      }
    } else {
      updates.ContactField[fieldKey] = "";
    }
  });

  await Promise.all(contactFieldPromises);
};

/**
 * Check and match existing contact sets
 */
export const checkMatchingContactSets = async (
  importedJSON: any,
  contactSetsData: any,
  updates: Record<string, Record<string, string>>,
  setAutomaticallySelectedFields: React.Dispatch<React.SetStateAction<string[]>>
): Promise<void> => {
  const contactSetPromises = Object.entries(
    importedJSON.Bag.External.ContactSet || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    if (contactSetsData?.contactSets?.length > 0) {
      updates.ContactSet[fieldKey] = contactSetsData.contactSets[0].uuid;
      setAutomaticallySelectedFields((prev) => [...prev, fieldKey]);
    } else {
      updates.ContactSet[fieldKey] = "";
    }
  });

  await Promise.all(contactSetPromises);
};

/**
 * Check and match existing reporting templates
 */
export const checkMatchingReportingTemplates = async (
  importedJSON: any,
  findMatchingTemplates: (data: any) => Promise<any>,
  updates: Record<string, Record<string, string>>,
  setAutomaticallySelectedFields: React.Dispatch<React.SetStateAction<string[]>>
): Promise<void> => {
  const reportingTemplatePromises = Object.entries(
    importedJSON.Bag.External.ReportingTemplate || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    const matchingTemplatesData = await findMatchingTemplates(
      JSON.parse(fieldKey)
    );
    if (matchingTemplatesData?.data?.data?.length > 0) {
      const matchingTemplate = matchingTemplatesData.data.data[0];
      updates.ReportingTemplate[fieldKey] = matchingTemplate.uuid;
      setAutomaticallySelectedFields((prev) => [...prev, fieldKey]);
      matchingTemplate.fields.forEach((field: any) => {
        const fieldData = {
          ...JSON.parse(fieldKey).fields.find(
            (f: any) => f.label === field.label
          ),
        };
        delete fieldData.uuid;
        delete fieldData.order;
        if (fieldData) {
          updates.ReportingTemplateField[JSON.stringify(fieldData)] =
            field.uuid;
        }
      });
    } else {
      updates.ReportingTemplate[fieldKey] = "";
    }
  });

  await Promise.all(reportingTemplatePromises);
};

/**
 * Check and match existing reporting topics
 */
export const checkMatchingTopics = async (
  importedJSON: any,
  reportingTopicsData: any,
  updates: Record<string, Record<string, string>>,
  setAutomaticallySelectedFields: React.Dispatch<React.SetStateAction<string[]>>
): Promise<void> => {
  const topicPromises = Object.entries(
    importedJSON.Bag.External.Topic || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    const matchingTopic = reportingTopicsData?.data.find((topic: any) => {
      const topicData = JSON.parse(fieldKey);
      return topic.label === topicData.label;
    });
    if (matchingTopic) {
      updates.Topic[fieldKey] = matchingTopic.uuid;
      setAutomaticallySelectedFields((prev) => [...prev, fieldKey]);
    } else {
      updates.Topic[fieldKey] = "";
    }
  });

  await Promise.all(topicPromises);
};

/**
 * Orchestrates all field matching operations
 */
export const handleCheckMatchingFields = async (
  importedJSON: any,
  queries: {
    findMatchingContactFields: (data: any) => Promise<any>;
    findMatchingTemplates: (data: any) => Promise<any>;
    contactSetsData: any;
    reportingTopicsData: any;
  },
  setDeployProgress: (progress: string) => void,
  setAutomaticallySelectedFields: React.Dispatch<React.SetStateAction<string[]>>
): Promise<Record<string, Record<string, string>>> => {
  setDeployProgress("Checking existing fields...");

  const updates: Record<string, Record<string, string>> = importedJSON.Bag
    .External || createEmptyFieldUpdates();

  setDeployProgress("Checking required fields...");

  // Check all field types in parallel
  await Promise.all([
    checkMatchingContactFields(
      importedJSON,
      queries.findMatchingContactFields,
      updates,
      setAutomaticallySelectedFields
    ),
    checkMatchingContactSets(
      importedJSON,
      queries.contactSetsData,
      updates,
      setAutomaticallySelectedFields
    ),
    checkMatchingReportingTemplates(
      importedJSON,
      queries.findMatchingTemplates,
      updates,
      setAutomaticallySelectedFields
    ),
    checkMatchingTopics(
      importedJSON,
      queries.reportingTopicsData,
      updates,
      setAutomaticallySelectedFields
    ),
  ]);

  setTimeout(() => {
    setDeployProgress("");
  }, 700);

  return updates;
};
